// Third Party
import * as React from 'react';
import ReactDOM from 'react-dom';
import { Transition, TransitionChild } from '@headlessui/react';
import Image from 'next/image';
import { GalleryAsset } from '@gql';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';

// UI
import { IconNames } from '@components/icon';
import { ButtonIcon } from '@components/button-icon';

type Props = {
  modalIsOpen: boolean;
  closeModal: VoidFunction;
  originalIndex: number;
  galleryImages: Pick<GalleryAsset, 'description' | 'url'>[];
  altTag: string;
};

export const ImageGalleryModal: React.FC<Props> = ({
  modalIsOpen,
  galleryImages,
  closeModal,
  originalIndex,
  altTag,
}) => {
  const galleryImagesCount = galleryImages.length - 1;
  const [galleryIndex, setGalleryIndex] = React.useState(originalIndex);
  const handleCloseModal = (): void => {
    setGalleryIndex(0);
    closeModal();
  };

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      setGalleryIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (event.key === 'ArrowRight') {
      setGalleryIndex((prevIndex) =>
        Math.min(prevIndex + 1, galleryImages.length - 1)
      );
    }
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (modalIsOpen) {
      document.body.classList.add('modalActive');
      window.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [modalIsOpen]);

  return ReactDOM.createPortal(
    <Transition
      show={modalIsOpen}
      className="fixed inset-0 overflow-hidden"
      style={{ zIndex: 2000 }}
      as="div"
    >
      <div className="flex items-center justify-center min-h-screen">
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 transition-opacity">
            <div className="absolute inset-0 bg-black opacity-60"></div>
          </div>
        </TransitionChild>

        <TransitionChild
          className="fixed inset-0 bg-white transform transition-all w-full h-full"
          as="div"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div className="absolute inset-0 px-4 sm:px-0 w-full h-full lg:hidden">
            <div
              className="fixed flex flex-row justify-start top-2 left-2 w-full"
              style={{ zIndex: 3000 }}
            >
              <ButtonIcon
                ariaDescribedby="close image mural"
                icon={IconNames.X}
                onClick={handleCloseModal}
                variant="outlined"
                size="sm"
                color="gray"
                className="inline-flex items-center p-4 h-4 w-4 justify-center bg-white"
                isCircle={true}
              />
            </div>
            <div
              className="h-full pb-16 pt-10 space-y-6 overflow-y-auto scroll-smooth"
              ref={(el) => {
                if (el) {
                  const imageElement = el.children[galleryIndex];
                  if (imageElement) {
                    imageElement.scrollIntoView({ behavior: 'instant' });
                  }
                }
              }}
            >
              {galleryImages.map((galleryImage, index) => (
                <div key={index} className="w-full aspect-[3/2] relative">
                  <Image
                    layout="fill"
                    src={galleryImage.url!}
                    className="object-center object-cover"
                    alt={altTag}
                    placeholder="blur"
                    loader={imagekitLoader}
                    blurDataURL={GRAY_PIXEL_DATA_URL}
                    sizes="calc(100vw - (2 * 20px))"
                    loading="eager"
                  />
                </div>
              ))}
            </div>
          </div>
          <div
            className="hidden lg:block absolute inset-0"
            style={{ height: '95%' }}
          >
            <div className="relative flex flex-row justify-center items-center p-4">
              <ButtonIcon
                ariaDescribedby="close image mural"
                icon={IconNames.X}
                onClick={handleCloseModal}
                variant="outlined"
                size="md"
                color="gray"
                className="inline-flex items-center p-4 h-10 w-10 justify-center"
                isCircle={true}
              />
              <span className="mx-auto text-gray-main">{`${galleryIndex + 1} / ${
                galleryImagesCount + 1
              }`}</span>
            </div>

            <div className="flex flex-row mx-auto w-full h-[calc(100%-4rem)] px-4">
              <div className="flex flex-col justify-center h-full">
                <ButtonIcon
                  ariaDescribedby="previous image"
                  icon={IconNames.CHEVRON_LEFT}
                  onClick={() => {
                    if (galleryIndex === 0) {
                      setGalleryIndex(galleryImagesCount);
                    } else {
                      setGalleryIndex(galleryIndex - 1);
                    }
                  }}
                  size="lg"
                  color="gray"
                  variant="text"
                />
              </div>

              <div className="mx-auto w-full h-full lg:w-3/4 relative">
                <div className="relative w-full h-full">
                  <Image
                    layout="fill"
                    src={galleryImages[galleryIndex].url!}
                    className="object-center object-cover"
                    alt={altTag}
                    objectFit={'contain'}
                    placeholder="blur"
                    loader={imagekitLoader}
                    blurDataURL={GRAY_PIXEL_DATA_URL}
                    sizes="75vw"
                    loading="eager"
                  />
                </div>
              </div>

              <div className="flex flex-col justify-center h-full">
                <ButtonIcon
                  ariaDescribedby="previous image"
                  icon={IconNames.CHEVRON_RIGHT}
                  onClick={() => {
                    if (galleryIndex === galleryImagesCount) {
                      setGalleryIndex(0);
                    } else {
                      setGalleryIndex(galleryIndex + 1);
                    }
                  }}
                  size="lg"
                  color="gray"
                  variant="text"
                />
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Transition>,
    document.getElementById('modal-root') as Element
  );
};
